import { getTelegramWebApp } from '../telegram'

export type ThemePreference = 'system' | 'light' | 'dark'
const STORAGE_KEY = 'expense_admin_theme'
const CHANGE_EVENT = 'expense-theme-change'
let sessionPreference: ThemePreference | null = null

export function getThemePreference(): ThemePreference {
  if (sessionPreference) return sessionPreference
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch { /* Storage can be unavailable in a WebView. */ }
  return 'dark'
}

export function getResolvedTheme(): 'light' | 'dark' {
  const preference = getThemePreference()
  if (preference !== 'system') return preference
  return getTelegramWebApp()?.colorScheme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
}

function applyTheme() {
  const theme = getResolvedTheme()
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.style.colorScheme = theme
  const background = getComputedStyle(document.documentElement).getPropertyValue('--background').trim()
  const color = background ? `hsl(${background})` : theme === 'dark' ? '#101014' : '#f8fafc'
  const probe = document.createElement('span')
  probe.style.color = color
  document.body.appendChild(probe)
  const rgb = getComputedStyle(probe).color.match(/\d+/g)
  probe.remove()
  const hex = rgb ? `#${rgb.slice(0, 3).map((value) => Number(value).toString(16).padStart(2, '0')).join('')}` : undefined
  if (hex) {
    try {
      const webApp = getTelegramWebApp()
      if (webApp?.initData) {
        webApp.setHeaderColor?.(hex)
        webApp.setBackgroundColor?.(hex)
      }
    } catch { /* Older Telegram clients can reject custom header colors. */ }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', hex)
  }
}

export function setThemePreference(preference: ThemePreference) {
  try { localStorage.setItem(STORAGE_KEY, preference) } catch { /* Keep the current session usable. */ }
  sessionPreference = preference
  applyTheme()
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

export function initializeTheme() {
  applyTheme()
  const update = () => {
    applyTheme()
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', update)
  window.addEventListener('storage', () => { sessionPreference = null; update() })
  getTelegramWebApp()?.onEvent?.('themeChanged', update)
}

export function subscribeToTheme(listener: () => void) {
  window.addEventListener(CHANGE_EVENT, listener)
  return () => window.removeEventListener(CHANGE_EVENT, listener)
}
